'use client';

import { useEffect, useRef, useState } from 'react';
import {  IPost } from '../../models/ICategory';
import { useMutation } from '@tanstack/react-query';
import { apiService } from '../../services/ApiService';
import RadioSelected from './svg/RadioSelected';
import RadioUnselected from './svg/RadioUnselected';
import Loader from './svg/Loader';
import CheckboxSelected from './svg/CheckboxSelected';
import CheckboxUnselected from './svg/CheckboxUnselected';
import { twMerge } from 'tailwind-merge';
import { IPoll, IPollQuestion } from '../../models/IPoll';
import Participants from './svg/Participants';
import { v7 as uuidv7 } from 'uuid';
import PollDateRenderer from '../voting/PollDateRenderer';
import { socialIcons } from '@/utils/socials';
import Share from './svg/socials/Share';
import CopyLink from './svg/socials/CopyLink';
import Link from 'next/link';
import { appConfig } from '@/config';
import { deleteQueryParams, getQueryParam } from '@/utils/querystring';

export default function Poll({data, showCreatedOn = false, collectionSlug}: {data: {poll: IPoll, pollUniqueCode: string}; showCreatedOn?: boolean; collectionSlug?: string;}) {


  const [deviceId ,setDeviceId] = useState<string>('');
  
  useEffect(() => {
    let id = uuidv7();
    const key = 'deviceId';
    const item = localStorage.getItem(key);
    if (item) id = item;
    else localStorage.setItem(key, id);
    setDeviceId(id);
    

  },[]);


useEffect(() => {
  const id = getQueryParam('id');

  if (!id || data.pollUniqueCode !== id) {
    return;
  };

  const elem = document.getElementById(id);

  if (!elem) return;

  elem.scrollIntoView({
    behavior: 'smooth',
    block: 'center',
  });
}, [data.pollUniqueCode]);

  const QuestionJsx = ({question}:{question: IPollQuestion}) => {
    switch(question.type) {
      case 1: {
        return <SingleSelection collectionSlug={collectionSlug} showCreatedOn={showCreatedOn} data={question} pollUniqueCode={data.pollUniqueCode!} hasVoted={Boolean(data.poll?.hasVoted)} deviceId={deviceId} endsOn={data.poll.endsOn}/>
      }
      case 2: {
        return <MultiSelection collectionSlug={collectionSlug} showCreatedOn={showCreatedOn} data={question} pollUniqueCode={data.pollUniqueCode!} hasVoted={Boolean(data.poll?.hasVoted)} deviceId={deviceId} endsOn={data.poll.endsOn}/>
      }
      case 3: {
        return <Text collectionSlug={collectionSlug} showCreatedOn={showCreatedOn} data={question} pollUniqueCode={data.pollUniqueCode!} hasVoted={Boolean(data.poll?.hasVoted)} deviceId={deviceId} endsOn={data.poll.endsOn}/>;
      } 
      default: return null;
    }
  }
  return (
<div className='flex flex-col gap-[24px] w-full h-fit'>
  {data.poll?.questions.map(q => <QuestionJsx question={q}/>)}
      </div>
  )
}



function SingleSelection({data, pollUniqueCode, hasVoted, deviceId, endsOn, showCreatedOn,collectionSlug}: {collectionSlug?: string; showCreatedOn: boolean,endsOn?: string; deviceId: string; hasVoted: boolean, data: IPollQuestion; pollUniqueCode: string;}) {

  const [completed, setCompleted] = useState(hasVoted || (endsOn ? (new Date(endsOn).getTime() <= Date.now()) : false));
  const [answer, setAnswer] = useState<number | null>(null);
    const [otherAnswer, setOtherAnswer] = useState({show: false, input: ''});

  const {
    mutate,
    isPending,
    data: completedData,
    isError,
    error,
    reset
  } = useMutation({
    mutationFn: () => {
      if (isError) reset();
      const isOther = data.showOtherOption && otherAnswer.show && otherAnswer.input;
      const otherOptionId = data.options.find(o => o.isOther);
      return apiService.votePoll({
        deviceId,
        pollUniqueCode, 
        answers: [
          {
            pollQuestionId: data.id, 
            optionIds: [isOther ? otherOptionId?.id! : answer!], 
            ...(isOther ? {otherAnswer: otherAnswer.input} : {})
          }
        ]})
    }, onSuccess: data => {
      setCompleted(true);
    }
  })


  const handleSelection = (id: number, isOther = false) => {
    if (isOther) {
      setOtherAnswer(prevState => ({...prevState, show: true}));
      setAnswer(null);
      return;
    }
    if (answer === id) return;
    setAnswer(id);
    setOtherAnswer(prevState => ({...prevState, show: false}));
  }


  const disableSubmission = completed || (data.showOtherOption && otherAnswer.show ? !otherAnswer.input.length : !answer)
  return (
    <div id={pollUniqueCode} className='flex flex-col gap-[24px] rounded-[12px] bg-white py-[32px] px-[16px] shadow-pollCard dark:bg-tertiary border border-[#f8f8f8] dark:border-[#3e3e3e]'>

        <div className='flex items-start w-full justify-between gap-[24px] flex-wrap'>
          <span className='h5'>{data.promptAr}</span>
          <div className='flex items-center gap-[12px] justify-between w-full tablet:w-fit tablet:justify-start'>

          {showCreatedOn ? <PollDateRenderer value={data.createdOn}/> : null}
          {collectionSlug ? <SocialsSharePoll slug={collectionSlug} id={pollUniqueCode} /> : null}
          </div>
        </div>
      


      {completed && (completedData || data) ? (
        <div className='flex flex-col gap-[16px] w-full'>
          {(completedData?.questions.find(q => q.id === data.id) || completedData?.questions[0] || data).options.map(o => {
            const isSelected = Boolean(completed ? o.isSelected : otherAnswer.show ? o.isOther : answer === o.id);
            return (
             <SelectionResultOption isSelected={isSelected} data={o}/>
          )})}
        </div>
      ) : (
        <>

  <div className='flex flex-col gap-[16px] w-full'>
    {(completedData?.questions.find(q => q.id === data.id) || completedData?.questions[0] || data).options.map(o => {
      const isSelected = o.isOther ? otherAnswer.show : answer === o.id;
      return (
        <div className='bg-gray-0 dark:bg-[#2a1535] w-full flex items-center py-[10px] px-[16px] gap-[12px] rounded-[8px]'>
        <div className={twMerge('grid place-items-center cursor-pointer', completed && 'cursor-not-allowed')} onClick={() => completed ? undefined : handleSelection(o.id, o.isOther)}>
          {isSelected ? <RadioSelected/> : <RadioUnselected/>}
        </div>
        <span className='sm font-medium'>{o.labelAr}</span>
        </div>               
    )})}

  {data.showOtherOption && otherAnswer.show ? (
    <textarea 
    className='w-full bg-gray-0 dark:bg-[#2a1535] p-[12px] rounded-[8px] h-[130px] sm !resize-none'
    placeholder='اكتب رأيك هنا...'
    value={otherAnswer.input}
    onChange={({target: {value}}) => setOtherAnswer(prevState => ({...prevState, input: value}))}
    
    />
  ) : null}

  </div>
      </>
    )}

    {isError ? <span className='text-danger'>{String(error.message || error)}</span> : null}
    {isPending ? <Loader className='mx-auto'/> : (
      <div className='flex items-center justify-between w-full'>

      <button disabled={disableSubmission} onClick={() => {
        if (disableSubmission) return;
        mutate();
      }} className='grid place-items-center w-fit bg-primary px-[24px] h-[42px] disabled:text-black text-white dark:disabled:text-white rounded-[8px] disabled:bg-gray-100 dark:disabled:bg-quinary disabled:cursor-not-allowed'>تصويت</button>
      <div className='flex items-center gap-[6px]'>
    <span className='w-fit whitespace-nowrap sm'>{completedData?.submissionCount || data.answerCount || 0} إجمالي المشاركات</span>
    <Participants/>
        </div>

  </div>
    )}
  
    </div>
  )
} 

function SelectionResultOption({data, isSelected}: {data: IPollQuestion['options'][0]; isSelected: boolean;}) {
const [progress, setProgress] = useState(0);
  const target = data.percentage;

  useEffect(() => {
    if (progress >= target) return;

    const interval = setInterval(() => {
      setProgress(prev => {
        const step = 1;
        const nextValue = prev + step;
        
        if (nextValue >= target) {
          clearInterval(interval);
          return target;
        }
        return nextValue;
      });
    }, 10);

    return () => clearInterval(interval);
  }, [target]);

  return (
        <div className={twMerge('w-full flex items-center justify-between bg-[#f8f8f8] dark:bg-[#2A1535] rounded-[8px] min-h-[40px] relative')}>
            <div className={twMerge('pr-[16px] w-full min-w-0 py-[6px] flex-1 h-full min-h-[40px] flex items-center justify-start bg-gray-0 dark:bg-[#341A42] rounded-[8px] flex-wrap', progress === 0 && '!bg-transparent', isSelected && 'bg-[#ffe6ff] dark:bg-[#590059]')} style={{width: `${progress}%`}}>
              <span className={twMerge('w-full max-w-[80%]',isSelected ? 'text-primary dark:text-primary' : 'text-black dark:text-white')}>{data.labelAr}</span>
              </div>

              <span className={twMerge('absolute left-[16px] top-[50%] translate-y-[-50%]', isSelected && 'text-primary dark:text-primary')}>{progress}%</span>
        </div>
  )
}

function MultiSelection({data, pollUniqueCode, hasVoted, deviceId, endsOn, showCreatedOn,collectionSlug}: {collectionSlug?: string, showCreatedOn: boolean,endsOn?: string; deviceId: string; hasVoted: boolean, data: IPollQuestion; pollUniqueCode: string;}) {

  const [completed, setCompleted] = useState(hasVoted || (endsOn ? (new Date(endsOn).getTime() <= Date.now()) : false));
  const [answer, setAnswer] = useState<number[]>([]);
  const [otherAnswer, setOtherAnswer] = useState({show: false, input: ''});

  const {
    mutate,
    isPending,
    data: completedData,
    reset,
    isError,
    error
  } = useMutation({
    mutationFn: () => {
      if (isError) reset();
      const hasOther = data.showOtherOption && otherAnswer.show && otherAnswer.input;
      const otherOptionId = data.options.find(o => o.isOther)?.id;
      return apiService.votePoll({
        deviceId,
        pollUniqueCode, 
        answers: [
          {
            pollQuestionId: data.id, 
            optionIds: [...answer, ...(hasOther && otherOptionId ? [otherOptionId] : [])], 
            ...(hasOther ? {otherAnswer: otherAnswer.input} : {})
          }
        ]})
    }, onSuccess: data => {
      setCompleted(true);
    }
  })


  const handleSelection = (id: number, isOther = false) => {
    if (isOther) {
      setOtherAnswer(prevState => ({...prevState, show: !prevState.show}));
      return;
    }
    const selected = answer.includes(id);
    if (selected) {
      const newState = structuredClone(answer);
      const index = newState.findIndex(x => x === id);
      newState.splice(index,1);
      setAnswer(newState)
    } else {
      setAnswer(prevState => [...prevState, id])
    }
  }

  const disableSubmission = completed || Boolean(data.showOtherOption && otherAnswer.show ? !otherAnswer.input.length : !answer.length);

  return (
    <div id={pollUniqueCode} className={twMerge('flex flex-col gap-[24px] rounded-[12px] bg-white py-[32px] px-[16px] shadow-pollCard dark:bg-tertiary border border-[#f8f8f8] dark:border-[#3e3e3e]')}>
      <div className='flex items-center gap-[12px]'>

     <div className='flex items-start w-full justify-between gap-[24px] flex-wrap'>
          <span className='h5'>{data.promptAr}</span>
          <div className='flex items-center gap-[12px] justify-between w-full tablet:w-fit tablet:justify-start'>

          {showCreatedOn ? <PollDateRenderer value={data.createdOn}/> : null}
          {collectionSlug ? <SocialsSharePoll slug={collectionSlug} id={pollUniqueCode} /> : null}
          </div>
        </div>

      </div>

         {completed && (completedData || data) ? (
        <div className='flex flex-col gap-[16px] w-full'>
          {(completedData?.questions.find(q => q.id === data.id) || completedData?.questions[0] || data).options.map(o => {
            const isSelected = Boolean(completed && data.selectedOptionIds?.length ? data.selectedOptionIds.includes(o.id) : o.isOther ? otherAnswer.show : answer.includes(o.id));
            return (
             <SelectionResultOption isSelected={isSelected} data={o}/>
          )})}
        </div>
      ) : (
        <>

  <div className='flex flex-col gap-[16px] w-full'>
    {(completedData?.questions.find(q => q.id === data.id) || completedData?.questions[0] || data).options.map(o => {
      const isSelected = o.isOther ? otherAnswer.show : answer.includes(o.id);
      return (
      <div className='bg-gray-0 dark:bg-[#2a1535] w-full flex items-center py-[10px] px-[16px] gap-[12px] rounded-[8px]'>
        <div className={twMerge('grid place-items-center cursor-pointer', completed && 'cursor-not-allowed')} onClick={() => completed ? undefined : handleSelection(o.id, o.isOther)}>
          {isSelected ? <CheckboxSelected/> : <CheckboxUnselected/>}
        </div>
        <span className='sm font-medium'>{o.labelAr}</span>
        </div>               
    )})}
      {data.showOtherOption && otherAnswer.show ? (
    <textarea 
    className='w-full bg-gray-0 dark:bg-[#2a1535] p-[12px] rounded-[8px] h-[130px] sm !resize-none'
    placeholder='اكتب رأيك هنا...'
    value={otherAnswer.input}
    onChange={({target: {value}}) => setOtherAnswer(prevState => ({...prevState, input: value}))}
    
    />
  ) : null}

    

  </div>
  </>
      )}

{isError ? <span className='text-danger'>{String(error.message || error)}</span> : null}
    {isPending ? <Loader className='mx-auto'/> : (
  <div className='flex items-center justify-between w-full'>

      <button disabled={disableSubmission} onClick={() => {
        if (disableSubmission) return;
        mutate();
      }} className='grid place-items-center w-fit bg-primary px-[24px] h-[42px] disabled:text-black text-white dark:disabled:text-white rounded-[8px] disabled:bg-gray-100 dark:disabled:bg-quinary disabled:cursor-not-allowed'>تصويت</button>
  <div className='flex items-center gap-[6px]'>
    <span className='w-fit whitespace-nowrap sm'>{completedData?.submissionCount || data.answerCount || 0} إجمالي المشاركات</span>
    <Participants/>
        </div>

  </div>
    )}
    </div>
  )
} 


function Text({data, pollUniqueCode, hasVoted, deviceId, endsOn, showCreatedOn, collectionSlug}: {collectionSlug?: string; showCreatedOn: boolean,endsOn?: string; deviceId: string, hasVoted: boolean, data: IPollQuestion; pollUniqueCode: string;}) {

  const [completed, setCompleted] = useState(hasVoted || (endsOn ? (new Date(endsOn).getTime() <= Date.now()) : false));
  const [answer, setAnswer] = useState<string>('');

  const {
    mutate,
    isPending,
    data: completedData,
    reset,
    isError,
    error
  } = useMutation({
    mutationFn: () => {
      if (isError) reset();
      return apiService.votePoll({pollUniqueCode, answers: [{pollQuestionId: data.id, answerText: answer!}], deviceId})
    },
    onSuccess: data => {
      setCompleted(true);
    }
  })

  const disableSubmission = !answer || completed;


  return (
    <div id={pollUniqueCode} className='flex flex-col gap-[24px] rounded-[12px] bg-white py-[32px] px-[16px] shadow-pollCard dark:bg-tertiary border border-[#f8f8f8] dark:border-[#3e3e3e]'>

          <div className='flex items-start w-full justify-between gap-[24px] flex-wrap'>
          <span className='h5'>{data.promptAr}</span>
          <div className='flex items-center gap-[12px] justify-between w-full tablet:w-fit tablet:justify-start'>

          {showCreatedOn ? <PollDateRenderer value={data.createdOn}/> : null}
          {collectionSlug ? <SocialsSharePoll slug={collectionSlug} id={pollUniqueCode} /> : null}
          </div>
        </div>


  <div className='flex flex-col gap-[16px] w-full'>
      <div className='bg-gray-0 w-full flex items-center gap-[12px] rounded-[8px] resize-none'>
        <textarea 
        disabled={completed}
        className='w-full bg-gray-0 dark:bg-[#2a1535] p-[12px] rounded-[8px] h-[130px] sm !resize-none'
        placeholder='اكتب رأيك هنا...'
        value={completed ? data.answerText : answer}
        onChange={({target: {value}}) => completed ? undefined : setAnswer(value)}
        
        />

 
        </div>               

       {completed && (completedData || data) ? (
          <div className='rounded-[8px] w-full bg-[#FFE6FF] dark:bg-[#590059] flex items-center p-[16px] min-h-[40px]'>
            <span className='text-primary sm font-medium'>تم إرسال إجابتك بنجاح! شكرًا لمشاركتك، رأيك مهم بالنسبة لنا.</span>
          </div>
        ) : null}
  </div>

{isError ? <span className='text-danger'>{String(error.message || error)}</span> : null}
    {isPending ? <Loader className='mx-auto'/> : (
  <div className='flex items-center justify-between w-full'>

      <button disabled={disableSubmission} onClick={() => {
        if (disableSubmission) return;
        mutate();
      }} className='grid place-items-center w-fit bg-primary px-[24px] h-[42px] disabled:text-black text-white dark:disabled:text-white rounded-[8px] disabled:bg-gray-100 dark:disabled:bg-quinary disabled:cursor-not-allowed'>تصويت</button>
  <div className='flex items-center gap-[6px]'>
    <span className='w-fit whitespace-nowrap sm'>{completedData?.submissionCount || data.answerCount || 0} إجمالي المشاركات</span>
    <Participants/>
        </div>

  </div>
    )}
    </div>
  )
} 


function SocialsSharePoll({className, slug, id}: {className?: string; slug: string; id: string}) {
  const socials = socialIcons.filter(s => ![1,4,5, 8, 10, 11, 12].includes(s.type));
  const [show, setShow] = useState(false);
  const [copied, setCopied] = useState(false);
  const ref = useRef<any>();
  const btnRef = useRef<any>();

  const handleClick = (e: any) => {
    if (ref.current && !ref.current.contains(e.target) && !btnRef.current.contains(e.target)) setShow(false);
  }

  useEffect(() => {
    window.addEventListener('mousedown',handleClick);
    return () => {
      window.removeEventListener('mousedown', handleClick);
    }
  },[]);

  const handleCopy = () => {
    navigator.clipboard.writeText(`${appConfig.NEXT_PUBLIC_BASE_URL}/voting/${slug}?id=${id}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 1.5 * 1000);
  }

  return (
    <div className='w-fit relative translate-y-[-8px] cursor-pointer'>
      <div  ref={btnRef} className={twMerge('grid place-items-center aspect-square h-[40px] p-[8px] rounded-[4px] bg-gray-0 dark:bg-quaternary')} onClick={() => setShow(!show)}>

    <Share/>
      </div>
        <div className={twMerge('h-[40px] absolute hidden items-center top-0 left-full px-[6px] border border-gray-0 dark:border-lightBorder rounded-[12px] bg-white dark:bg-quaternary', show && 'flex')} ref={ref}>
          {/* <Close className='h-[18px] w-[18px] [&>*]:fill-black dark:[&>*]:fill-white' onClick={() => setShow(false)}/> */}
        <div className={twMerge('flex justify-center w-full gap-[24px] desktop:gap-[16px] desktop:py-[24px] [&>*]:grid [&>*]:place-items-center [&>*]:rounded-[4px] [&>*]:[&>*]:h-[28px] [&>*]:[&>*]:w-[28px] [&>*]:cursor-pointer', className)}>
          <div className={twMerge('grid place-items-center h-fit w-fit relative')}>
            <CopyLink className='[&>*]:!w-[28px] [&>*]:!h-[28px]' onClick={() => copied ? undefined : handleCopy()} />
            {copied ? <div className='absolute left-0 top-0 !w-fit translate-y-[-36px] sm text-gray-1000 dark:text-white py-[2px] px-[5px] bg-gray-0 dark:bg-lightBorder rounded-[4px] h-fit grid place-items-center overflow-visible'><span>نسخ</span></div> : null}
          </div>
          {socials.map(({ShareComponent,getShareLink,type, title}, i) => <Link title={title} key={type} target='_blank' href={getShareLink(`${appConfig.NEXT_PUBLIC_BASE_URL}/voting/${slug}?id=${id}`)}><ShareComponent keepOriginal className='[&>*]:h-full [&>*]:w-full'/></Link>)}
        </div>
        </div>
    </div>
  )
}